import { z } from 'zod';
import { paginationQuerySchema, paginated } from './common.js';
/*
 * ⚠️ The SAME vocabulary the incident queue uses, imported rather than
 * restated. Two lists a reader moves between must not offer different words for
 * one question — and a second enum is how one of them quietly gains a value.
 */
import { incidentProvenanceFilterSchema, riskLevelSchema } from './incident.js';
export const recommendationStatusSchema = z.enum([
    'GENERATED',
    'ACKNOWLEDGED',
    'ACTIONED',
    'DISMISSED',
    'SUPERSEDED',
]);
export const recommendationSortSchema = z.enum(['createdAt', 'riskScore', 'status']);
/**
 * A query-string boolean where BOTH values are meaningful.
 *
 * Deliberately NOT `z.coerce.boolean()`. That helper is truthiness-based, so
 * `?hasOutcome=false` arrives as the string "false", which is truthy, and
 * coerces to `true` — risk R8. For `approvalRequired` that is merely a latent
 * bug because nobody asks for the negative case; for `hasOutcome` it would
 * invert the primary use of the filter, returning exactly the recommendations
 * the operator was trying to exclude.
 *
 * Left as a plain string enum rather than `.transform(v => v === 'true')`,
 * because a transform makes it a `ZodEffects` which the OpenAPI generator drops
 * — the parameter would vanish from the published spec while still working, and
 * `openapi-query-params.test.ts` rightly fails the build for it. Converting in
 * the service costs one comparison and keeps the spec honest; the enum also
 * documents the two accepted strings, which `boolean` would not.
 */
const queryBoolean = z.enum(['true', 'false']).optional();
export const recommendationListQuerySchema = paginationQuerySchema
    .extend({
    sort: recommendationSortSchema.default('createdAt'),
    status: recommendationStatusSchema.optional(),
    riskLevel: riskLevelSchema.optional(),
    actionCategory: z.string().optional(),
    incidentId: z.string().uuid().optional(),
    vehicleId: z.string().uuid().optional(),
    /* The latent bug the `queryBoolean` note above predicted. Now uses it. */
    approvalRequired: queryBoolean,
    /**
     * `false` finds recommendations still awaiting an outcome — the worklist that
     * makes label collection tractable. Omitted means "either".
     */
    hasOutcome: queryBoolean,
    /**
     * Whether the recommendation was replaced by a newer one for the same
     * incident.
     *
     * Kept ORTHOGONAL to `hasOutcome` rather than folded into it: a parameter
     * named "has outcome" that also silently dropped superseded rows would be
     * lying about what it filters. The awaiting-outcome worklist composes the
     * two, and says so in its label.
     *
     * `false` is the useful direction. Only GENERATED recommendations are ever
     * superseded (see `createSuperseding`), so a superseded row is one no
     * operator ever acted on — acknowledging or actioning it would have moved it
     * out of GENERATED and made it ineligible. There is no judgement to record
     * about it, and the successor carries the current one.
     */
    superseded: queryBoolean,
    explanationSource: z.string().optional(),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
    search: z.string().trim().min(1).optional(),
    /**
     * How the incident behind this recommendation was FOUND — design D2.
     *
     * ⚠️ **Provenance lives on the INCIDENT, never on the recommendation.** A
     * recommendation is advice ABOUT an incident; `incidentId` is NOT NULL, so the
     * relation always resolves and this filter can never silently drop a row the
     * way a nullable join would. Copying `sources` onto the recommendation would
     * be a second answer to one question — and the copy is what goes stale.
     *
     * ⚠️ **Defaults to `RULE`, matching the incident queue**, so an absent
     * parameter means what this endpoint meant before anomaly detection and the
     * two queues cannot disagree about what a reader is looking at. Before this
     * they DID: the queue hid an anomaly incident while its advice sat in this
     * list, so a reader could act on advice and then reach a record the incident
     * list swore did not exist.
     */
    foundBy: incidentProvenanceFilterSchema.default('RULE'),
})
    .strict();
export const recommendationListItemSchema = z.object({
    id: z.string().uuid(),
    incidentId: z.string().uuid(),
    vehicleId: z.string().uuid(),
    /**
     * Which lorry and which person this is about, in words.
     *
     * The queue and the detail page could name neither: `recommendedAction` is a
     * rendered template that happens to mention the vehicle, so the name was
     * readable only by reading the sentence, and the driver was never nameable at
     * all. On a screen whose whole purpose is a conversation with a specific
     * person, "the driver" is a real limitation rather than a tidy abstraction.
     *
     * **Both are NULLABLE, and neither null means missing data.** The joins are by
     * id with no tenant in the key — the two the vehicle-transfer design calls
     * out. A vehicle moved to another branch takes its identity with it and leaves
     * this recommendation behind, so the name is withheld rather than read out of
     * a branch that no longer owns it; a driver row deleted or belonging to
     * another tenant resolves to nothing for the same reason. The client renders
     * the absence rather than inventing a placeholder.
     */
    vehicleName: z.string().nullable(),
    vehicleRegistration: z.string().nullable(),
    driverName: z.string().nullable(),
    actionCategory: z.string(),
    recommendedAction: z.string(),
    status: recommendationStatusSchema,
    riskLevel: riskLevelSchema,
    riskScore: z.number().int(),
    approvalRequired: z.boolean(),
    explanationSource: z.string(),
    llmEnhanced: z.boolean(),
    llmFallbackUsed: z.boolean(),
    /**
     * Whether an outcome has been recorded, and which disposition if so.
     *
     * Carried on the LIST item rather than requiring a per-row fetch: without it
     * an operator cannot tell which recommendations still need recording without
     * opening each one, which is the friction that starves the outcome dataset.
     */
    hasOutcome: z.boolean(),
    outcomeDisposition: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const recommendationListResponseSchema = paginated(recommendationListItemSchema);
const evidenceSchema = z.object({
    id: z.string().uuid(),
    kind: z.string(),
    reference: z.string(),
    detail: z.record(z.unknown()),
});
/** The deterministic core plus the (optional) LLM enhancement, kept distinct. */
export const recommendationDetailSchema = recommendationListItemSchema.extend({
    /** When the terminal decision was taken. Null until ACTIONED or DISMISSED. */
    decidedAt: z.string().nullable(),
    driverId: z.string().uuid().nullable(),
    recommendationType: z.string(),
    deterministic: z.object({
        explanation: z.string(),
        reasoningSteps: z.array(z.string()),
        /**
         * The policy conditions that matched, structurally.
         *
         * The same facts `reasoningSteps` already carries as sentences — but a
         * sentence has to be parsed to be labelled, and the parse is what makes
         * `event.measurements.sustainedSeconds EXISTS undefined (actual: 240)` the
         * worst line on the recommendation page. With the fields intact a client can
         * ask `POLICY_FIELDS` for the label and the unit and say "sustained for 4
         * minutes".
         *
         * `expected` and `operator` are carried but are NOT for the summary: the
         * threshold belongs to the policy screen, and an `EXISTS` check has no
         * expected value to show. They are here for the technical record.
         *
         * EMPTY on every recommendation generated before the column existed, which
         * is an absence for the life of the retention window rather than a claim
         * that nothing matched.
         */
        matchedConditions: z.array(z.object({
            field: z.string(),
            operator: z.string(),
            expected: z.unknown(),
            actual: z.unknown(),
        })),
        generationVersion: z.string(),
    }),
    enhancement: z
        .object({
        headline: z.string().nullable(),
        operatorSummary: z.string().nullable(),
        confidenceWording: z.string().nullable(),
        selectedExplanation: z.string().nullable(),
        promptVersion: z.string().nullable(),
    })
        .nullable(),
    evidence: z.array(evidenceSchema),
    risk: z
        .object({
        riskAssessmentId: z.string().uuid().nullable(),
        riskLevel: riskLevelSchema,
        riskScore: z.number().int(),
    })
        .and(z.object({ factors: z.record(z.unknown()).nullable() })),
    policyId: z.string().uuid().nullable(),
    policyVersionId: z.string().uuid().nullable(),
    /**
     * Enough about the incident and the rule to say a SENTENCE about each before
     * either is opened.
     *
     * The detail page shows both in place, behind a disclosure that fetches
     * nothing until it is opened. That laziness is right and it left the two rows
     * saying "The incident this belongs to" and "The rule that produced it" —
     * controls carrying no information, giving a reader no reason to use them.
     *
     * These are what let the COLLAPSED row answer: *"Part of an open incident ·
     * first seen 2 hours ago · seen 9 times"*. Two of them change what a reader
     * does rather than saving a click — one over-speed is a conversation and the
     * ninth is a pattern, and advice issued by a version that is no longer in
     * force reads as the platform misapplying its own policy unless it says so.
     *
     * DETAIL only, not the list item. The queue shows none of this, and widening
     * the list projection would join a policy and an incident for twenty rows to
     * decorate none of them.
     *
     * Every one is nullable except the counts: the incident join is by id with no
     * tenant in the key, and a recommendation may name no policy at all.
     */
    /**
     * The event this recommendation is about — its measurements and where it
     * happened.
     *
     * The page led with the ACTION and put the one number a reader wants inside a
     * sentence. `speedKph`, `speedLimitKph` and `overByKph` are all on the event
     * and were on no screen: the limit, what was recorded and the difference are
     * the story, and they belong in the headline rather than in prose.
     *
     * The FIRST contributing event, not a merge and not a pick. An incident may
     * group several — three harsh brakings — and choosing "the worst" would be a
     * judgement the platform has not made anywhere else. `eventCount` says when
     * there are more, so the strip is never read as the whole picture.
     *
     * `location` is NULL far more often than not — 30 of 123 events in the
     * development database carry one — so the absence is the common case and the
     * screen must say which it is: no position sent, rather than none recorded.
     */
    primaryEvent: z
        .object({
        id: z.string().uuid(),
        eventType: z.string(),
        occurredAt: z.string(),
        measurements: z.record(z.unknown()),
        /** Raw as stored; the client validates before it plots anything. */
        location: z.record(z.unknown()).nullable(),
    })
        .nullable(),
    /** How many events the incident groups, so one set of numbers is not read as all of them. */
    eventCount: z.number().int(),
    incidentStatus: z.string().nullable(),
    incidentOpenedAt: z.string().nullable(),
    incidentOccurrenceCount: z.number().int().nullable(),
    policyName: z.string().nullable(),
    policyVersionNumber: z.number().int().nullable(),
    /**
     * Whether the version that produced this is still the one in force.
     *
     * NULL means "not known" and is a third answer, not a false: the version may
     * have been discarded, or the recommendation may name none. Rendering an
     * unknown as "no longer in force" would put a warning on advice that is
     * perfectly current.
     */
    policyVersionIsActive: z.boolean().nullable(),
    /** What kind of vehicle it is, when the tenant classifies its fleet. */
    vehicleClassName: z.string().nullable(),
});
/** History = the superseded chain for an incident + this recommendation's audit. */
export const recommendationHistoryItemSchema = z.object({
    id: z.string().uuid(),
    status: recommendationStatusSchema,
    actionCategory: z.string(),
    recommendedAction: z.string(),
    riskScore: z.number().int(),
    generationVersion: z.string(),
    explanationSource: z.string(),
    createdAt: z.string(),
});
export const recommendationHistoryResponseSchema = z.object({
    recommendationId: z.string().uuid(),
    incidentId: z.string().uuid(),
    chain: z.array(recommendationHistoryItemSchema),
    audit: z.array(z.object({
        id: z.string().uuid(),
        action: z.string(),
        actorId: z.string().uuid().nullable(),
        at: z.string(),
    })),
});
// ─────────────────── Recommendation outcome (Stage 8A feedback) ───────────────────
export const dispositionReasonSchema = z.enum([
    'ACTIONED_EFFECTIVE',
    'ACTIONED_INEFFECTIVE',
    'ALREADY_HANDLED',
    'NOT_ACTIONABLE',
    'NO_ACTION_REQUIRED',
    'FALSE_POSITIVE',
    'DUPLICATE',
    'SUPERSEDED_BY_UPDATE',
    'DEFERRED',
    'ESCALATED',
]);
export const effectivenessSchema = z.enum(['RESOLVED', 'PARTIAL', 'NO_EFFECT', 'MADE_WORSE']);
// Label confidence — ordinal LOW<MEDIUM<HIGH, optional (omitted = not stated).
export const operatorConfidenceSchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
// Who performed the work — structured type + optional free-text identity + optional User link.
export const performedByTypeSchema = z.enum([
    'DRIVER',
    'MECHANIC',
    'FLEET_MANAGER',
    'OPERATOR',
    'EXTERNAL_WORKSHOP',
    'AUTOMATED_INTEGRATION',
    'OTHER',
]);
// WHY a recommendation succeeded/failed — mandatory (distinct axis from disposition).
export const rootCauseCategorySchema = z.enum([
    'RULE',
    'DATA_QUALITY',
    'GPS',
    'SENSOR',
    'VEHICLE',
    'DRIVER',
    'MAINTENANCE',
    'USER_PROCESS',
    'DUPLICATE_EVENT',
    'UNKNOWN',
    'OTHER',
]);
export const recordOutcomeRequestSchema = z
    .object({
    dispositionReason: dispositionReasonSchema,
    rootCauseCategory: rootCauseCategorySchema, // mandatory
    effectiveness: effectivenessSchema.optional(),
    operatorConfidence: operatorConfidenceSchema.optional(),
    actionCompletedAt: z.coerce.date().optional(),
    performedByType: performedByTypeSchema.optional(),
    performedByName: z.string().trim().min(1).max(200).optional(),
    performedByUserId: z.string().uuid().optional(),
    repairCostMinor: z.number().int().nonnegative().optional(),
    repairCurrency: z
        .string()
        .regex(/^[A-Z]{3}$/, 'ISO 4217 code')
        .optional(),
    repairDurationMinutes: z.number().int().nonnegative().optional(),
    estimatedDowntimeAvoidedMinutes: z.number().int().nonnegative().optional(),
    maintenanceOutcome: z.record(z.unknown()).optional(),
    note: z.string().trim().max(2000).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    // OTHER on either axis requires a meaningful note (≥4 trimmed chars). Mirrors the
    // repository guard and the SQL CHECK.
    const meaningful = typeof v.note === 'string' && v.note.trim().length >= 4;
    if (v.dispositionReason === 'OTHER' && !meaningful) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['note'],
            message: 'a meaningful note is required when dispositionReason is OTHER',
        });
    }
    if (v.rootCauseCategory === 'OTHER' && !meaningful) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['note'],
            message: 'a meaningful note is required when rootCauseCategory is OTHER',
        });
    }
});
export const recommendationOutcomeSchema = z.object({
    id: z.string().uuid(),
    recommendationId: z.string().uuid(),
    incidentId: z.string().uuid(),
    dispositionReason: dispositionReasonSchema,
    rootCauseCategory: rootCauseCategorySchema,
    effectiveness: effectivenessSchema.nullable(),
    operatorConfidence: operatorConfidenceSchema.nullable(),
    actionCompletedAt: z.string().nullable(),
    performedByType: performedByTypeSchema.nullable(),
    performedByName: z.string().nullable(),
    performedByUserId: z.string().uuid().nullable(),
    repairCostMinor: z.number().int().nullable(),
    repairCurrency: z.string().nullable(),
    repairDurationMinutes: z.number().int().nullable(),
    estimatedDowntimeAvoidedMinutes: z.number().int().nullable(),
    falsePositive: z.boolean(),
    note: z.string().nullable(),
    recordedById: z.string().uuid().nullable(),
    recordedAt: z.string(),
    updatedAt: z.string(),
});
export const policyOutcomeStatSchema = z.object({
    policyVersionId: z.string().uuid().nullable(),
    generationVersion: z.string().nullable(),
    total: z.number().int(),
    falsePositives: z.number().int(),
    actionedEffective: z.number().int(),
    falsePositiveRate: z.number(),
});
export const outcomeStatsResponseSchema = z.object({
    stats: z.array(policyOutcomeStatSchema),
});
// Consolidated summary for GET outcomes/stats (Stage 8A) — the enumerated metrics.
export const outcomeSummaryResponseSchema = z.object({
    totalOutcomes: z.number().int(),
    falsePositives: z.number().int(),
    falsePositiveRate: z.number(),
    effectiveActions: z.number().int(),
    ineffectiveActions: z.number().int(),
    dispositionCounts: z.record(z.number().int()),
    rootCauseCounts: z.record(z.number().int()),
    confidenceCounts: z.record(z.number().int()),
    averageTimeToActionSeconds: z.number().nullable(),
});
export const rootCauseStatSchema = z.object({
    rootCauseCategory: rootCauseCategorySchema,
    total: z.number().int(),
    falsePositives: z.number().int(),
    share: z.number(),
});
export const rootCauseStatsResponseSchema = z.object({
    stats: z.array(rootCauseStatSchema),
});
export const confidenceStatSchema = z.object({
    operatorConfidence: z.enum(['LOW', 'MEDIUM', 'HIGH', 'NOT_STATED']),
    total: z.number().int(),
    falsePositives: z.number().int(),
    falsePositiveRate: z.number(),
});
export const confidenceStatsResponseSchema = z.object({
    stats: z.array(confidenceStatSchema),
});
/**
 * Precision over time — the shape that shows whether tuning WORKED.
 *
 * Every other outcome analytic is a point-in-time aggregate: it can say
 * "precision is 88%" but not "precision went from 71% to 88% after the
 * harsh-braking threshold changed". This is the one that demonstrates the
 * feedback loop functions rather than merely exists.
 *
 * Buckets are UTC and keyed by `recordedAt` — when the operator made the
 * judgement, matching every other outcome analytic. A day boundary in UTC will
 * not align with an operator's local day; that is a reporting convention, not
 * an accident.
 */
export const outcomeTrendBucketSchema = z.enum(['day', 'week', 'month']);
export const outcomeTrendPointSchema = z.object({
    /** UTC start of the bucket, ISO-8601. Weeks start Monday (ISO-8601). */
    bucketStart: z.string(),
    total: z.number().int(),
    falsePositives: z.number().int(),
    actionedEffective: z.number().int(),
    /** 0..1. Zero when the bucket is empty — read `total` before trusting it. */
    falsePositiveRate: z.number(),
});
export const outcomeTrendResponseSchema = z.object({
    bucket: outcomeTrendBucketSchema,
    /**
     * Contiguous between the first and last bucket that HAS data, with empty
     * buckets filled in. A quiet week is information, and omitting it would let a
     * line chart join March to June as though nothing had happened in between.
     * Bounded by observed data, so it cannot grow without limit.
     */
    points: z.array(outcomeTrendPointSchema),
});
// Operational timing — all values DERIVED server-side (never stored). Milliseconds.
export const timingStatsResponseSchema = z.object({
    outcomesWithAction: z.number().int(),
    outcomesTotal: z.number().int(),
    medianTimeToActionMs: z.number().nullable(),
    p90TimeToActionMs: z.number().nullable(),
    medianTimeToRecordMs: z.number().nullable(),
});
// ─────────────────── Outcome evidence attachments (Stage 8F) ───────────────────
export const evidenceTypeSchema = z.enum([
    'PHOTO',
    'MAINTENANCE_INVOICE',
    'WORK_ORDER',
    'INSPECTION_REPORT',
    'PDF_DOCUMENT',
    'DASHCAM_VIDEO',
    'CAN_DIAGNOSTIC',
    'OTHER',
]);
export const evidenceStatusSchema = z.enum(['PENDING', 'AVAILABLE', 'QUARANTINED', 'DELETED']);
export const initiateEvidenceRequestSchema = z.object({
    evidenceType: evidenceTypeSchema,
    fileName: z.string().trim().min(1).max(255),
    contentType: z.string().trim().min(1).max(255),
    byteSize: z.number().int().positive(),
    checksumSha256: z.string().regex(/^[a-f0-9]{64}$/i, 'must be a hex sha256'),
    caption: z.string().trim().max(500).optional(),
});
export const evidenceAttachmentSchema = z.object({
    id: z.string().uuid(),
    recommendationId: z.string().uuid(),
    outcomeId: z.string().uuid(),
    evidenceType: evidenceTypeSchema,
    fileName: z.string(),
    contentType: z.string(),
    byteSize: z.number().int(),
    status: evidenceStatusSchema,
    caption: z.string().nullable(),
    uploadedById: z.string().uuid().nullable(),
    uploadedAt: z.string(),
});
export const initiateEvidenceResponseSchema = z.object({
    attachment: evidenceAttachmentSchema,
    upload: z.object({
        url: z.string(),
        method: z.literal('PUT'),
        expiresInSeconds: z.number().int(),
        /**
         * Headers the client must send verbatim on the PUT. Carries the content
         * type and, where the store supports it, the expected SHA-256 — so the
         * browser never needs to know it is talking to something S3-shaped.
         */
        headers: z.record(z.string()).default({}),
    }),
});
export const evidenceListResponseSchema = z.object({
    attachments: z.array(evidenceAttachmentSchema),
});
export const evidenceDownloadUrlResponseSchema = z.object({
    url: z.string(),
    expiresInSeconds: z.number().int(),
});
export const evidenceCoverageResponseSchema = z.object({
    byType: z.array(z.object({ evidenceType: evidenceTypeSchema, count: z.number().int() })),
});
// Effectiveness by performer type (Stage 8G) — separates who did the work from who recorded it.
export const performerStatsResponseSchema = z.object({
    stats: z.array(z.object({
        performedByType: z.enum([
            'DRIVER',
            'MECHANIC',
            'FLEET_MANAGER',
            'OPERATOR',
            'EXTERNAL_WORKSHOP',
            'AUTOMATED_INTEGRATION',
            'OTHER',
            'NOT_STATED',
        ]),
        total: z.number().int(),
        resolved: z.number().int(),
        falsePositives: z.number().int(),
        resolvedRate: z.number(),
    })),
});
// Aggregate business impact for ROI dashboards (Stage 8H). Cost grouped by
// currency (never summed across); cost-avoided derived downstream, not stored.
export const businessImpactSummaryResponseSchema = z.object({
    outcomesWithImpact: z.number().int(),
    totalRepairDurationMinutes: z.number().int(),
    totalEstimatedDowntimeAvoidedMinutes: z.number().int(),
    repairCostByCurrency: z.array(z.object({
        currency: z.string(),
        totalMinor: z.number().int(),
        count: z.number().int(),
    })),
});
// ── lifecycle ───────────────────────────────────────────────────────────────
/**
 * What an operator does with a recommendation.
 *
 * `ACKNOWLEDGED`, `ACTIONED` and `DISMISSED` existed in the status enum from the
 * beginning and NOTHING could set them — no endpoint, no service method, no
 * control — so every row in every database was `GENERATED` or `SUPERSEDED`, both
 * written by the engine. That is also why recommendation outcomes are empty: the
 * outcome form sat at the end of a workflow nobody was guided through, because
 * the workflow did not exist.
 */
export const recommendationActionSchema = z.enum(['acknowledge', 'action', 'dismiss']);
/**
 * Legal transitions, declared once and shared by the server and the screen.
 *
 * A single table so the buttons a reader is offered and the transitions the API
 * will accept cannot drift — offering a control the server refuses is the
 * mistake this codebase has made twice, and a lifecycle is where it is easiest
 * to make a third time.
 *
 * **`SUPERSEDED` is absent from every `from` list deliberately.** It is written
 * by the engine when a newer recommendation replaces this one, and a superseded
 * row is history: acting on it would record a decision about advice the platform
 * has already withdrawn.
 */
export const RECOMMENDATION_TRANSITIONS = {
    acknowledge: { from: ['GENERATED'], to: 'ACKNOWLEDGED' },
    action: { from: ['GENERATED', 'ACKNOWLEDGED'], to: 'ACTIONED' },
    dismiss: { from: ['GENERATED', 'ACKNOWLEDGED'], to: 'DISMISSED' },
};
/** Whether an action is legal from a status. The one place that decides. */
export function canTransition(action, from) {
    return RECOMMENDATION_TRANSITIONS[action].from.includes(from);
}
export const recommendationActionRequestSchema = z
    .object({
    /**
     * Why — free text, kept for the audit trail.
     *
     * Required for `dismiss` at the CALL SITE rather than here, because the same
     * shape serves three verbs. A dismissal with no reason is the one that
     * destroys the signal outcome capture exists to gather: "the platform was
     * wrong" and "not worth doing today" are different facts and only the note
     * separates them.
     */
    note: z.string().trim().min(1).max(500).optional(),
})
    .strict();
export const recommendationActionResponseSchema = z.object({
    id: z.string().uuid(),
    status: recommendationStatusSchema,
    decidedAt: z.string().nullable(),
});
/**
 * The same three verbs, applied to a selection.
 *
 * Bounded at 100 deliberately. A queue action is a person clearing what they can
 * see; anything larger is a migration, and a migration dressed as a button is
 * how somebody dismisses a quarter of their history by accident.
 */
export const BULK_RECOMMENDATION_LIMIT = 100;
export const bulkRecommendationActionRequestSchema = z
    .object({
    ids: z.array(z.string().uuid()).min(1).max(BULK_RECOMMENDATION_LIMIT),
    /**
     * One reason for the whole selection.
     *
     * Weaker than a reason each, and honest about it: "these six are all the
     * same sensor fault" is a real thing to say, and a shared note is far better
     * than the nothing that a bulk action without one would record. Still
     * REQUIRED for a dismissal, for the same reason it is on a single one.
     */
    note: z.string().trim().min(1).max(500).optional(),
})
    .strict();
/**
 * What happened to each one.
 *
 * Per item, never a count. Some of a selection will legitimately fail — one was
 * superseded while the page was open, another needs approval the caller does not
 * have — and a response saying "6 updated" when two did not is the exact shape
 * of lie this platform avoids everywhere else. The screen reports both halves.
 */
export const bulkRecommendationActionResponseSchema = z.object({
    succeeded: z.array(z.string().uuid()),
    failed: z.array(z.object({
        id: z.string().uuid(),
        /** Shown to the reader as-is; written for them, not for a log. */
        reason: z.string(),
    })),
});
// ── policy learning (V2) ────────────────────────────────────────────────────
/**
 * What recorded outcomes say about one version of one rule.
 *
 * The precision table this extends showed a raw `policyVersionId` and a rate.
 * An operations manager cannot act on a UUID, and a rate alone does not say
 * what to DO — a high false-positive rate caused by SENSOR is a data problem
 * where the same rate caused by RULE is a threshold problem, and retuning the
 * wrong one is worse than doing nothing.
 *
 * Attributed to the VERSION, never the policy: the id is snapshotted on the
 * outcome, so editing a rule tomorrow cannot rewrite what yesterday's version
 * produced.
 */
export const policyLearningVerdictSchema = z.discriminatedUnion('outcome', [
    z.object({
        outcome: z.literal('INSUFFICIENT_EVIDENCE'),
        needed: z.number().int(),
        have: z.number().int(),
    }),
    z.object({ outcome: z.literal('PERFORMING') }),
    z.object({
        outcome: z.literal('NEEDS_ATTENTION'),
        falsePositiveRate: z.number(),
        dominantCause: z.string().nullable(),
        likelyFix: z.enum(['RULE_TUNING', 'DATA_QUALITY', 'UNCLEAR']),
    }),
]);
export const policyLearningRowSchema = z.object({
    policyVersionId: z.string().uuid().nullable(),
    /**
     * Null when the version no longer exists — a never-activated draft may be
     * discarded, and the outcomes it produced deliberately survive it. The screen
     * shows the id rather than inventing a name: visibly unresolved beats
     * silently renamed.
     */
    policyId: z.string().uuid().nullable(),
    policyName: z.string().nullable(),
    eventType: z.string().nullable(),
    version: z.number().int().nullable(),
    /** Whether this is the version running now — retuning a superseded one is wasted work. */
    isActive: z.boolean(),
    total: z.number().int(),
    falsePositives: z.number().int(),
    actionedEffective: z.number().int(),
    /**
     * Null below the evidence floor.
     *
     * Two false positives out of three is 67% and means nothing; a rate stated on
     * three outcomes invites retuning a threshold that governs a fleet. The counts
     * are still returned — those are facts — and only the ratio is withheld, the
     * same way CAN operations withholds `kmPerL` under a distance floor.
     */
    falsePositiveRate: z.number().nullable(),
    rootCauses: z.array(z.object({ cause: z.string(), count: z.number().int() })),
    verdict: policyLearningVerdictSchema,
});
export const policyLearningResponseSchema = z.object({
    rows: z.array(policyLearningRowSchema),
    /** Echoed so the screen states the window it is describing rather than implying "always". */
    sinceDays: z.number().int().nullable(),
    minOutcomes: z.number().int(),
});
//# sourceMappingURL=recommendation.js.map